import { getDupes } from "lib/arrayUtils";
import styles from "./chatEntry.module.scss";
import Highlighter from "react-highlight-words";
import classnames from "classnames/bind";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { CHAT_ENTRY_TYPES } from "./chatConsts";
import { useTheme } from "contexts/ThemeContext";
import { useEffect, useState } from "react";
import { format, isBefore, sub } from "date-fns";
import { diceMap } from "components/Common/Icons/Dice";
import {
  Shuffle,
  ChevronDown,
  ChevronUp,
  Search,
  Eye,
  EyeOff,
  Pin,
  PencilIcon,
  MessageSquareText,
  VenetianMaskIcon,
} from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import {
  getSettingsDrawerBooleanValue,
  SETTINGS_DRAWER_BOOLEANS,
} from "components/Header/useSettingsDrawerBooleans";
import {
  findIndex,
  isArray,
  isEmpty,
  isNil,
  isObject,
  isString,
  noop,
} from "lodash";
import { Track } from "components/Characters/components/Track";
import { Button } from "components/Common/Button";
import { BaseImage, Image } from "components/Common/Image/Image";
import {
  COMPENDIUM_IFRAME_LOCAL_STORAGE_PATH,
  COMPENDIUM_IFRAME_LOCAL_STORAGE_PATH_RESET,
} from "contexts/IframeContext";
import { useLocalStorageSetter } from "hooks/useLocalStorage";
import { useChatHelper } from "contexts/ChatHelperContext";
import { PressureTrack } from "components/Common/PressureTrack";
import { ScrutinyTrack } from "components/Common/ScrutinyTrack";
import { uuid } from "lib/uid";
import { usePreviewImageModal } from "contexts/PreviewImageModalContext";
import { fpSet, fpUpdate } from "lib/fpLodash";
import { Textarea } from "components/Common/Textarea";
import { Input } from "components/Common/Input";
import { FooterButtons } from "components/Common/FooterButtons/FooterButtons";
import { parseLinkText } from "lib/stringUtils";

const classNameBuilder = classnames.bind(styles);

const MAX_TEXT_LENGTH = 100;

const TextChatEntry = ({
  editing,
  onUpdate,
  maximized,
  value = "",
  className,
}) => {
  const [showing, setShowing] = useState(maximized);

  useEffect(() => {
    setShowing(maximized);
  }, [maximized]);

  const valueTooBig = value.length > MAX_TEXT_LENGTH;

  const shownValue =
    valueTooBig && !showing
      ? value.substring(0, MAX_TEXT_LENGTH) + "..."
      : value;

  const parsedText = parseLinkText(shownValue, { linkClassName: styles["link"] });

  return (
    <div
      className={classNameBuilder("text-result", className, {
        pointer: valueTooBig,
      })}
      onClick={() => setShowing((prev) => !prev)}
    >
      {editing ? (
        <Textarea
          className={styles["edit-input"]}
          value={value}
          onChange={(e) =>
            onUpdate((entry) => fpSet("value", e.target.value, entry))
          }
          autoFocus
          onFocus={(e) => e.target?.select()}
        />
      ) : (
        <>
          {valueTooBig &&
            (showing ? (
              <ChevronUp className={styles["icon"]}>
                <Tooltip>Click to Collapse</Tooltip>
              </ChevronUp>
            ) : (
              <ChevronDown className={styles["icon"]}>
                <Tooltip>Click to Expand</Tooltip>
              </ChevronDown>
            ))}
          <span className={styles["text"]}>{parsedText}</span>
        </>
      )}
    </div>
  );
};

export const ListItemChatEntry = ({
  value: chatValue = {},
  openPreviewImageModal,
  onUpdate,
  editing,
  ...rest
}) => {
  const { value, name, details, breaks, image } = chatValue;

  return (
    <div className={styles["list-result-container"]}>
      {image && (
        <BaseImage
          className={styles["list-result-image"]}
          src={image}
          contain
          onClick={() => {
            openPreviewImageModal(image);
          }}
        />
      )}
      <div className={styles["list-result"]}>
        {editing ? (
          <Input
            value={name}
            onChange={(e) =>
              onUpdate((prev) => fpSet(["value", "name"], e.target.value, prev))
            }
          />
        ) : (
          <u>{name || ""}</u>
        )}
        {value && isArray(value) && (
          <Track
            value={value}
            breaks={breaks}
            setValueByIndex={
              editing
                ? (v, i) =>
                  onUpdate((prev) => fpSet(["value", "value", i], v, prev))
                : noop
            }
            setBreakByIndex={
              editing
                ? (v, i) =>
                  onUpdate((prev) => fpSet(["value", "breaks", i], v, prev))
                : noop
            }
            presentation={!editing}
          />
        )}
        {editing ? (
          <Textarea
            className={styles["edit-input"]}
            onChange={(e) =>
              onUpdate((prev) =>
                fpSet(["value", "details"], e.target.value, prev)
              )
            }
            value={details}
          />
        ) : (
          details && (
            <TextChatEntry
              {...rest}
              className={styles["list-details"]}
              value={details}
            />
          )
        )}
      </div>
    </div>
  );
};

const CHAT_ENTRY_CONTENT = {
  [CHAT_ENTRY_TYPES.DICE_ROLL]: ({ authUser, value, cut }) => {
    const rawVals = value.split(", ");
    rawVals.sort((a, b) => (a > b ? -1 : 1));
    let vals = [...rawVals];
    let cutVals = [];
    if (cut) {
      cutVals = vals.splice(0, Math.min(cut, vals.length - 1));
    }
    const cutText = cutVals.join("\u0020\u00A0 \u00A0\u0020");
    const valsText = vals.join("\u0020\u00A0·\u00A0\u0020");
    const dupes = getDupes(vals);
    const maxValue = Math.max(...vals);
    const DiceIcon = diceMap[maxValue];
    const chatOutcomeColorEnabled = getSettingsDrawerBooleanValue(
      SETTINGS_DRAWER_BOOLEANS.OUTCOME_COLOR_ENABLED,
      authUser
    );
    const chatTwistColorEnabled = getSettingsDrawerBooleanValue(
      SETTINGS_DRAWER_BOOLEANS.TWIST_COLOR_ENABLED,
      authUser
    );
    const chatDiceIconsVisible = getSettingsDrawerBooleanValue(
      SETTINGS_DRAWER_BOOLEANS.CHAT_DICE_ICONS_VISIBLE,
      authUser
    );
    const chatCutStrikeThroughEnabled = getSettingsDrawerBooleanValue(
      SETTINGS_DRAWER_BOOLEANS.CUT_STRIKE_THROUGH_ENABLED,
      authUser
    );
    const chatCutResultsDividerEnabled = false;

    return (
      <div className={styles["roll-result"]}>
        {chatDiceIconsVisible && (
          <>
            <DiceIcon
              className={classNameBuilder(
                chatOutcomeColorEnabled
                  ? {
                    triumph: maxValue === 6,
                    conflict: maxValue === 4 || maxValue === 5,
                    disaster: maxValue < 4,
                  }
                  : {}
              )}
            />
            {dupes.length > 0 && (
              <div className={styles["shuffle-container"]}>
                <Shuffle
                  className={classNameBuilder("shuffle", {
                    colored: chatTwistColorEnabled,
                  })}
                />
              </div>
            )}
          </>
        )}
        {cut > 0 && (
          <>
            <div
              className={classNameBuilder("dice-text", "cut", {
                "strike-through": chatCutStrikeThroughEnabled,
              })}
            >
              {cutText}
            </div>
            {chatCutResultsDividerEnabled && (
              <div className={styles["divider"]} />
            )}
          </>
        )}

        {chatTwistColorEnabled ? (
          <Highlighter
            className={styles["dice-text"]}
            highlightClassName={styles["highlighted"]}
            highlightTag={"span"}
            searchWords={[dupes.join("\u0020\u00A0 \u00A0\u0020")]}
            textToHighlight={valsText}
          />
        ) : (
          valsText
        )}
      </div>
    );
  },
  [CHAT_ENTRY_TYPES.TEXT]: TextChatEntry,
  [CHAT_ENTRY_TYPES.COMPENDIUM]: ({
    value,
    setExpanded = noop,
    openPreviewImageModal,
    ...rest
  }) => {
    const { key, history, label, image, disableWideImage } = value;
    const setInitialPath = useLocalStorageSetter(
      COMPENDIUM_IFRAME_LOCAL_STORAGE_PATH
    );
    const setPathReset = useLocalStorageSetter(
      COMPENDIUM_IFRAME_LOCAL_STORAGE_PATH_RESET
    );
    const { compendiumPortalPropsRef } = useChatHelper();

    if (!key || !label || !history) {
      return <TextChatEntry {...rest} value={"Unknown article..."} />;
    }

    const openCompendium = () => {
      setInitialPath(history);
      setPathReset(uuid());
      compendiumPortalPropsRef?.current?.setIsPortalComponentRendered(true);
      setExpanded(false);
    };

    const ImageComponent = !disableWideImage ? Image : BaseImage;

    return (
      <div className={styles["compendium"]}>
        <div className={styles["compendium-content"]}>
          {image && (
            <div className={styles["compendium-image"]}>
              <ImageComponent
                src={image}
                width={80}
                height={80}
                contain={!disableWideImage}
                onClick={openCompendium}
              />
              <Search
                className={styles["search-icon"]}
                onClick={(e) => {
                  e.preventDefault();
                  openPreviewImageModal(image);
                }}
              >
                <Tooltip position="top">Click to Preview Image</Tooltip>
              </Search>
            </div>
          )}
          <div className={styles["compendium-label"]} onClick={openCompendium}>
            {label}
          </div>
        </div>
        <Button onClick={openCompendium}>Open Article in Compendium</Button>
      </div>
    );
  },
  [CHAT_ENTRY_TYPES.IMAGE]: ({ value, openPreviewImageModal }) => {
    const Component = value?.shouldShowBackground ? Image : BaseImage;
    return (
      <Component
        src={value?.src}
        className={styles["image-entry"]}
        contain
        onClick={() => {
          openPreviewImageModal(value?.src);
        }}
      />
    );
  },
  [CHAT_ENTRY_TYPES.UNKNOWN]: ({ value, ...rest }) => {
    let shownValue = "Unknown Entry...";

    if (!isObject && isString(value)) {
      shownValue = value;
    }

    return <TextChatEntry {...rest} value={shownValue} />;
  },
  [CHAT_ENTRY_TYPES.LIST_ITEM]: ListItemChatEntry,
  [CHAT_ENTRY_TYPES.PRESSURE_GAUGE]: ({
    value: pressureGauge = {},
    editing,
    onUpdate,
  }) => {
    return (
      <div className={classNameBuilder("pressure-results", { editing })}>
        <PressureTrack
          value={pressureGauge}
          setValue={
            editing
              ? (updater) =>
                onUpdate((entry) => fpUpdate("value", updater, entry))
              : noop
          }
          presentation={!editing}
        />
      </div>
    );
  },
  [CHAT_ENTRY_TYPES.SCRUTINY]: ({
    value: scrutiny = {},
    editing,
    onUpdate,
  }) => {
    return (
      <div className={classNameBuilder("pressure-results", { editing })}>
        <ScrutinyTrack
          value={scrutiny}
          setValue={
            editing
              ? (updater) =>
                onUpdate((entry) => fpUpdate("value", updater, entry))
              : noop
          }
          presentation={!editing}
        />
      </div>
    );
  },
};
const EDITABLE_TYPES_SET = new Set([
  CHAT_ENTRY_TYPES.LIST_ITEM,
  CHAT_ENTRY_TYPES.TEXT,
  CHAT_ENTRY_TYPES.PRESSURE_GAUGE,
  CHAT_ENTRY_TYPES.SCRUTINY,
]);

export const ChatEntry = ({
  entry,
  disableBorder,
  index,
  className,
  closeButton,
  setExpanded = noop,
  isPinned,
  togglePinnedChatEntry,
  showingPinned,
  maximized,
  setEntries,
  setPinnedEntries,
  onPingEntry,
}) => {
  const { user: authUser } = useAuth();
  const { themeClassNames } = useTheme();
  const {
    name = "",
    portrait,
    dateAdded,
    user = "",
    userId,
    value = "",
    type = CHAT_ENTRY_TYPES.TEXT,
    id: entryId,
    hidden,
    edited,
    origin,
    visibilityWhitelist: rawVisibilityWhitelist,
    ...rest
  } = entry;
  const visibilityWhitelist = rawVisibilityWhitelist || [];
  const hasWhitelist = !isEmpty(visibilityWhitelist);
  const { openPreviewImageModal } = usePreviewImageModal();

  let Content =
    CHAT_ENTRY_CONTENT[type] || CHAT_ENTRY_CONTENT[CHAT_ENTRY_TYPES.UNKNOWN];
  const editable = EDITABLE_TYPES_SET.has(type);

  const dateAddedObj = new Date(dateAdded);
  let time;
  try {
    time = format(dateAddedObj, "hh:mm");
  } catch (err) {
    time = "--";
  }

  const date =
    isBefore(dateAddedObj, sub(Date.now(), { days: 1 })) &&
    format(new Date(dateAdded), "MMM dd");

  const toggleEntryVisibility = () => {
    setEntries((prev) => {
      const copy = [...prev];

      let indexToUpdate = index;

      if (!isNil(entryId)) {
        indexToUpdate = findIndex(copy, (e) => entryId === e.id);
      }

      if (!isNil(indexToUpdate) && indexToUpdate !== -1) {
        const prevEntry = copy[indexToUpdate];
        copy[indexToUpdate] = { ...prevEntry, hidden: !prevEntry.hidden };
      }

      return copy;
    });
  };
  const [editing, setEditing] = useState();
  const [editState, setEditState] = useState(entry);

  useEffect(() => {
    if (editing) {
      setEditState(entry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, entry?.id]);

  const onUpdate = (updater) => {
    setEditState((prev) => {
      return updater(prev);
    });
  };

  const onSaveEdit = () => {
    const setter = (showingPinned ? setPinnedEntries : setEntries) || noop;
    setter((prev) => {
      const updated = fpSet(index, editState, prev);
      return fpSet(
        [index, "edited"],
        `${authUser?.displayName} (UID: ${authUser?.uid})`,
        updated
      );
    });
    setEditing(false);
  };

  return (
    <div
      className={classNameBuilder(
        "entry",
        ...themeClassNames,
        {
          "has-border": !disableBorder,
          even: index % 2 === 0,
          odd: index % 2 !== 0,
          "hide-overflow": type === CHAT_ENTRY_TYPES.SCRUTINY,
          "is-user-only-entry": hasWhitelist,
        },
        className
      )}
    >
      <u className={styles["label-container"]}>
        {hidden ? (
          <span>Hidden Entry ({user})</span>
        ) : (
          <>
            <Image
              src={portrait}
              className={styles["img"]}
              alt="portrait"
              round
            />
            <span className={styles["label"]}>{`${name} (${user})`}</span>
            {hasWhitelist && (
              <span>
                <VenetianMaskIcon
                  style={{
                    verticalAlign: "middle",
                    marginBottom: "2px",
                  }}
                />
                <Tooltip position="top-end">
                  This entry is only visible to{" "}
                  {visibilityWhitelist?.length === 1
                    ? "you"
                    : "certain players"}
                </Tooltip>
              </span>
            )}
            {date && <span className={styles["date"]}>{date}</span>}
            <span className={styles["date"]}>{time}</span>
          </>
        )}
        {!editing && (
          <>
            {togglePinnedChatEntry && (
              <Button
                className={styles["hidden-button"]}
                colorful={isPinned}
                round
                onClick={() => togglePinnedChatEntry(entry)}
              >
                <Pin />
                <Tooltip position={"top-end"}>
                  {isPinned ? "Un-pin" : "Pin"} Entry
                </Tooltip>
              </Button>
            )}
            {!closeButton &&
              Boolean(userId && authUser?.uid && authUser.uid === userId) &&
              !showingPinned && (
                <Button
                  style={togglePinnedChatEntry ? { marginLeft: "0" } : {}}
                  className={styles["hidden-button"]}
                  round
                  onClick={toggleEntryVisibility}
                >
                  {hidden ? <Eye /> : <EyeOff />}
                  <Tooltip position={"top-end"}>
                    {hidden ? "Show" : "Hide"} Entry
                  </Tooltip>
                </Button>
              )}
            {showingPinned && (
              <Button
                style={togglePinnedChatEntry ? { marginLeft: "0" } : {}}
                className={classNameBuilder("hidden-button")}
                round
                onClick={() => onPingEntry(entry)}
              >
                <MessageSquareText />
                <Tooltip position={"top-end"}>Send to Chat</Tooltip>
              </Button>
            )}
            {!closeButton && editable && (
              <Button
                style={togglePinnedChatEntry ? { marginLeft: "0" } : {}}
                className={classNameBuilder("hidden-button", {
                  visible: editing,
                })}
                round
                colorful={editing}
                onClick={() => setEditing((prev) => !prev)}
              >
                <PencilIcon />
                <Tooltip position={"top-end"}>
                  {editing ? "Stop Edit" : "Edit"}
                </Tooltip>
              </Button>
            )}
            {closeButton}
          </>
        )}
      </u>
      <div
        className={classNameBuilder("value", {
          animate: index === 0,
        })}
      >
        {!hidden && (
          <Content
            authUser={authUser}
            value={editing ? editState?.value : value}
            setExpanded={setExpanded}
            id={entryId}
            maximized={maximized}
            openPreviewImageModal={(val, { shouldKeepChatExpanded } = {}) => {
              openPreviewImageModal(val);
              if (!shouldKeepChatExpanded) {
                setExpanded(false);
              }
            }}
            editing={editing && editable}
            onUpdate={onUpdate}
            {...rest}
          />
        )}
      </div>
      {editing ? (
        <FooterButtons
          onCancel={() => setEditing(false)}
          onSubmit={() => onSaveEdit()}
          submitText="Save"
          submitButtonProps={{ colorful: true }}
          disablePadding
        />
      ) : (
        <>
          {edited && (
            <div className={styles["edited"]}>
              <span>
                [EDITED]
                <Tooltip boxClassName={styles["tooltip"]} position="top-end">
                  Last edit by: {edited}
                </Tooltip>
              </span>
            </div>
          )}
          {origin && <div className={styles["edited"]}>({origin})</div>}
        </>
      )}
    </div>
  );
};
